




import { GOOGLE_MAPS_MAP_ID } from "@/utils/Constants";
import { Vue, Component, Prop } from "vue-property-decorator";
import { Position } from "@/models/Position";
import { MILAN_LOCATION } from "@/utils/Map";
import { Polygon } from "@/models/MapObjects";

declare global {
  interface Window {
    google?: any;
  }
}

interface POI {
  lat: number;
  lng: number;
}

interface MarkerOptions {
  icon?: any;
  strokeColor?: string;
  strokeOpacity?: number;
  strokeWeight?: number;
  fillColor?: string;
  fillOpacity?: number;
}

interface PolygonOptions {
  strokeColor?: string;
  strokeOpacity?: number;
  strokeWeight?: number;
  fillColor?: string;
  fillOpacity?: number;
}

@Component({})
export default class VGoogleMap extends Vue {
  @Prop({ required: false, default: () => MILAN_LOCATION })
  readonly centerPosition!: POI;
  @Prop({ required: false, default: false })
  readonly fullscreenControl!: boolean;
  @Prop({ required: false, default: 13 }) readonly zoom!: number;
  @Prop({ required: false, default: false })
  readonly disableDefaultUI!: boolean;

  private map: any = null;
  private loaded = false;
  private markersMap: { [id: string]: any } = {};
  private markersMapListeners: { [id: string]: any } = {};
  private polygonsMap: { [id: string]: any } = {};
  private polygonsMapListeners: { [id: string]: any } = {};
  static DEFAULT_MAP_ZOOM = 10;
  static DEFAULT_MAP_SINGLE_POI_ZOOM = 15;

  private refresh = false;

  mounted() {
    this.initialized();
  }

  destroy() {
    this.clearMarkers();
  }

  addMarker(id: string, position: Position, options: MarkerOptions) {
    if (this.markersMap[id]) {
      return;
    }

    this.markersMap[id] = new window.google.maps.Marker({
      position: position,
      map: this.map,
      icon: options.icon,
      strokeColor: options.strokeColor,
      strokeOpacity: options.strokeOpacity,
      strokeWeight: options.strokeWeight,
      fillColor: options.fillColor,
      fillOpacity: options.fillOpacity,
    });

    this.markersMapListeners[id] = this.markersMap[id].addListener(
      "click",
      () => this.dispatchMarkerClickEvent(id)
    );
  }

  removeMarker(id: string) {
    if (!this.markersMap[id]) {
      return;
    }

    this.removeMarkerEventListener(id);
    this.markersMap[id].setMap(null);
    this.markersMap[id] = null;

    if (this.polygonsMap[id]) {
      this.removePolygonEventListener(id);
      this.polygonsMap[id].setMap(null);
      this.polygonsMap[id] = null;
    }

    this.refreshMap();
  }

  removePolygon(id: string) {
    if (!this.polygonsMap[id]) {
      return;
    }
    this.removePolygonEventListener(id);
    this.polygonsMap[id].setMap(null);
    this.polygonsMap[id] = null;

    this.refreshMap();
  }

  removeMarkerEventListener(id: string) {
    if (this.markersMapListeners[id]) {
      window.google.maps.event.removeListener(this.markersMapListeners[id]);
    }
  }

  removePolygonEventListener(id: string) {
    if (this.polygonsMapListeners[id]) {
      window.google.maps.event.removeListener(this.polygonsMapListeners[id]);
    }
  }

  dispatchMarkerClickEvent(id: string) {
    this.$emit("markerClick", id);
  }

  dispatchPolygonClickEvent(id: string) {
    this.$emit("polygonClick", id);
  }

  centerOnPosition(position: Position, zoom?: number) {
    this.map.setCenter(position);
    this.setMapZoom(zoom ? zoom : VGoogleMap.DEFAULT_MAP_ZOOM);
    this.refreshMap();
  }

  centerOnBounds(positions: Position[], zoom?: number) {
    let bounds = new window.google.maps.LatLngBounds();
    positions.forEach((position) => {
      if (position) {
        bounds.extend(
          new window.google.maps.LatLng(position.lat, position.lng)
        );
      }
    });
    this.map.fitBounds(bounds);
    if (zoom) {
      this.setMapZoom(zoom);
    } else if (positions.length === 1) {
      this.setMapZoom(VGoogleMap.DEFAULT_MAP_SINGLE_POI_ZOOM);
    }
    this.refreshMap();
  }

  clearMarkers() {
    if (!this.markersMap) {
      return;
    }

    for (const key in this.markersMap) {
      this.removeMarkerEventListener(key);
      this.markersMap[key]?.setMap(null);
      this.markersMap[key] = null;
    }

    if (!this.polygonsMap) {
      return;
    }

    for (const key in this.polygonsMap) {
      this.removePolygonEventListener(key);
      this.polygonsMap[key]?.setMap(null);
      this.polygonsMap[key] = null;
    }
  }

  updateMarker(id: string, position: Position, options?: MarkerOptions) {
    const marker = this.markersMap[id];
    if (marker) {
      if (options) {
        marker.setIcon(options.icon);
      }
      if (position) marker.setPosition(position);
    }
  }

  updatePolygon(id: string, paths?: any[], polygonOptions?: PolygonOptions) {
    const polygon = this.polygonsMap[id];
    if (polygon) {
      if (paths) polygon.setPath(paths);
      if (polygonOptions) polygon.setOptions(polygonOptions);
    }
  }

  drawPolygon(id: string, paths: any[], polygonOptions: PolygonOptions) {
    if (this.polygonsMap[id]) {
      this.removePolygonEventListener(id);
      this.polygonsMap[id].setMap(null);
      this.polygonsMap[id] = null;
    }

    const polygon = new window.google.maps.Polygon({
      paths: paths,
      strokeColor: polygonOptions.strokeColor,
      strokeOpacity: polygonOptions.strokeOpacity,
      strokeWeight: polygonOptions.strokeWeight,
      fillColor: polygonOptions.fillColor,
      fillOpacity: polygonOptions.fillOpacity,
      map: this.map,
    });
    this.polygonsMap[id] = polygon;
    this.polygonsMapListeners[id] = this.polygonsMap[id].addListener(
      "click",
      () => this.dispatchPolygonClickEvent(id)
    );
  }

  initialized() {
    if (this.loaded) {
      return;
    }
    this.map = new window.google.maps.Map(document.getElementById("map"), {
      center: this.centerPosition,
      fullscreenControl: this.fullscreenControl,
      zoom: this.zoom,
      mapId: GOOGLE_MAPS_MAP_ID,
      disableDefaultUI: this.disableDefaultUI,
    });
    this.loaded = true;
  }

  refreshMap() {
    this.map.setZoom(this.map.getZoom() - 1);
    this.map.setZoom(this.map.getZoom() + 1);
  }

  setMapZoom(zoom: number) {
    this.map.setZoom(zoom);
  }
}
